import type { IncidentContextI, SourceI } from '../types'

export interface CannedExchangeI {
  question: string
  answer: string
  sources: SourceI[]
  suggestedQuestions: string[]
}

/**
 * Builds a scripted Door Held Open conversation for sales demos,
 * interpolating real facility/floor/device names from the incident context.
 */
export const buildDhoConversation = (
  ctx: IncidentContextI
): CannedExchangeI[] => {
  const facility = ctx.facility_name || 'the facility'
  const floor = ctx.floor_name || 'the floor'
  const device = ctx.device_name || 'the door'

  return [
    {
      question: `A door held open alarm just came in on ${device}. What should I do first?`,
      answer: `Based on the **Door Held Protocol** for ${facility}, here's what you should do immediately:\n\n1. **Acknowledge the incident** in the system\n2. **Bring up relevant cameras** for ${device} on ${floor}\n3. Visually assess whether the door is being held open intentionally or if it's a malfunction\n\nSince ${device} is on ${floor}, this is classified as a **high priority** area. All exterior/perimeter doors, high-risk areas, and exits must be thoroughly investigated and escalated if you're unable to verify the situation.`,
      sources: [
        {
          content:
            'Door Held Protocol \u2014 The door held open alarms will come in via the Access Control system. All exterior/perimeter doors, high risk areas and exits are considered high priority and must be thoroughly investigated and escalated if unable to verify. GSOC Response: Acknowledge the incident. Bring up relevant cameras.',
          score: 0,
        },
      ],
      suggestedQuestions: [
        'Should I dispatch a guard?',
        'What if the door was propped open intentionally?',
        'Who do I escalate to at this location?',
      ],
    },
    {
      question: 'Should I dispatch a guard?',
      answer: `Yes, if you cannot verify the situation via camera, you should **dispatch a guard to investigate** the area around ${device} on ${floor}.\n\nKey protocol points while the guard is en route:\n\n- **Stay in constant contact** with the guard via radio\n- Ask the guard: **"Is assistance needed?"**\n  - If **yes**: follow the appropriate escalation protocol\n  - If **no**: direct the guard to **ensure the door is properly secured**\n\nOnce the guard confirms the door is secured, proceed to call ${facility}'s on-site management for follow-up.`,
      sources: [
        {
          content:
            'Door Held Protocol \u2014 If necessary, dispatch a guard to investigate the area of the alarm. GSOC stays in constant contact with the guard. Is assistance needed? If yes, follow appropriate protocol. If no, direct to ensure door is properly secured.',
          score: 0,
        },
      ],
      suggestedQuestions: [
        'What should I document after the guard responds?',
        'How do I tag this incident?',
        'What if the guard needs backup?',
      ],
    },
    {
      question: 'What should I document after the guard responds?',
      answer: `After the guard confirms ${device} is secured, complete the following:\n\n1. **Call ${facility}'s management** for follow-up on the incident\n2. **Record all details** of the request in the Dispatch System, including:\n   - Time of alarm and acknowledgment\n   - Camera observations\n   - Guard dispatch time and response\n   - Resolution (door secured, cause identified, etc.)\n3. **Tag "Security"** in the incident record\n\nThis ensures a complete audit trail for ${facility}'s security team.`,
      sources: [
        {
          content:
            'Door Held Protocol \u2014 Call location\'s management for follow up. Record details of request in Dispatch System. Tag "Security" in incident.',
          score: 0,
        },
      ],
      suggestedQuestions: [
        'Is there a time limit for door held alarms?',
        'What if management is unreachable?',
      ],
    },
  ]
}

export const SALES_CTA_RESPONSES = [
  'Contact HiveWatch Sales to see what HiveWatch GSOC OS can do for you!',
  'Want to see more? Reach out to HiveWatch Sales for a full demo of GSOC OS.',
  'This is just a preview! Talk to HiveWatch Sales to unlock the full SOP experience.',
  'Interested in AI-powered SOPs? Contact HiveWatch Sales to learn more.',
  'HiveWatch GSOC OS has a lot more to offer. Get in touch with Sales to see it all!',
]
